import { createContext, useContext, useState, useEffect, useCallback, useMemo, useReducer, useRef, type ReactNode } from 'react';
import type { Workspace, Location, UserRole, AccessibleWorkspace } from '@bakedshift/shared';
import { RoleHierarchy, normalizeRole } from '@bakedshift/shared';
import { apiGet, apiPost, setWorkspaceId, withWorkspace, getErrorMessage } from '@/lib/api-client';
import { isLocationInScope, shouldClearLocation, type LocationScope } from '@/lib/location-scope';
import { useAuth } from './AuthContext';

// Access level for locations
export type LocationAccessLevel = 'read' | 'write' | 'admin';

// User's location assignment with access level
export interface UserLocationAccess {
  locationId: string;
  location: Location;
  accessLevel: LocationAccessLevel;
}

// User's workspace membership with role and locations — the wire contract of
// GET /workspaces/me/accessible, owned by packages/shared.
export type UserWorkspaceAccess = AccessibleWorkspace;

// Extended Location type with access level for the switcher
export interface LocationWithAccess extends Location {
  accessLevel: string;
}

interface WorkspaceContextType {
  // Current workspace and location
  workspace: Workspace | null;
  location: Location | null;
  isLoading: boolean;

  // User's role in current workspace
  currentRole: UserRole | null;

  // Lists of accessible workspaces and locations
  accessibleWorkspaces: UserWorkspaceAccess[];
  accessibleLocations: LocationWithAccess[];

  // Permission helpers
  hasRole: (minimumRole: UserRole) => boolean;
  canAccessWorkspace: (workspaceId: string) => boolean;
  canAccessLocation: (locationId: string) => boolean;
  getWorkspaceRole: (workspaceId: string) => UserRole | null;
  getLocationAccessLevel: (locationId: string) => LocationAccessLevel | null;

  // Setters
  setWorkspace: (workspace: Workspace | null) => void;
  setLocation: (location: Location | null) => void;

  // Switching
  switchWorkspace: (workspaceId: string, options?: { skipAccessCheck?: boolean }) => Promise<boolean>;
  switchLocation: (locationId: string | null) => Promise<boolean>;

  // Refresh
  refreshWorkspace: () => Promise<void>;
  refreshLocation: () => Promise<void>;
  refreshAccessibleWorkspaces: () => Promise<void>;

  // Error state - consumers can display this
  error: string | null;
  clearError: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

interface WorkspaceProviderProps {
  children: ReactNode;
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Roles are read in the current names (the 2026-09-25 rename): a response
  // from a server not yet migrated can still say `admin` or `superuser`.
  // Reducers, so each setter stays a stable dispatch.
  const [currentRole, setCurrentRole] = useReducer((_: UserRole | null, r: unknown) => normalizeRole(r), null);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);
  const [accessibleWorkspaces, setAccessibleWorkspaces] = useReducer(
    (_: UserWorkspaceAccess[], list: UserWorkspaceAccess[]) => list.flatMap((w) => {
      const role = normalizeRole(w.role);
      return role === null ? [] : [{ ...w, role }];
    }),
    [],
  );

  // Compute accessible locations for current workspace (with access level)
  const accessibleLocations = useMemo((): LocationWithAccess[] => {
    if (!workspace) return [];
    const workspaceAccess = accessibleWorkspaces.find(w => w.workspace.id === workspace.id);
    if (!workspaceAccess) return [];

    // Map locations to include access level and full location structure
    return workspaceAccess.locations.map(loc => ({
      id: loc.id,
      name: loc.name,
      locationCode: loc.locationCode,
      isActive: loc.isActive,
      accessLevel: loc.accessLevel,
      workspaceId: workspace.id,
      publicTimesheetKey: '',
      allowedIpRanges: [],
      settings: {},
      createdAt: new Date(),
      updatedAt: new Date(),
    } as LocationWithAccess));
  }, [workspace, accessibleWorkspaces]);

  /**
   * The location scope of the CURRENT workspace, as one value, so that
   * "loaded and empty" cannot be confused with "not loaded yet".
   *
   * `accessibleLocations` above is `[]` in both cases, and the difference is
   * not cosmetic: `CreateWorkspaceModal` switches to a freshly created
   * workspace and calls `refreshAccessibleWorkspaces()` without awaiting it,
   * and every cold load has a window between the workspace landing in state
   * and `/workspaces/me/accessible` answering. Clearing the selection on an
   * empty array would flip the switcher label to "All Locations" and back on
   * both. The membership entry for this workspace is the honest signal that
   * the list has been resolved — its `locations` array is the whole answer
   * for this user (the server already returns every location of the
   * workspace to workspace admins), so `[]` from a present entry means
   * the workspace genuinely exposes none.
   */
  const locationScope = useMemo((): LocationScope => {
    const currentWorkspaceId = workspace?.id;
    if (!currentWorkspaceId) return { accessibleLocationIds: null };
    const resolved = accessibleWorkspaces.some(w => w.workspace.id === currentWorkspaceId);
    if (!resolved) return { accessibleLocationIds: null };
    return { accessibleLocationIds: accessibleLocations.map(loc => loc.id) };
  }, [workspace, accessibleWorkspaces, accessibleLocations]);

  /**
   * The workspace every in-flight request was issued under. Kept in a ref, not
   * in state, because it must be readable and writable synchronously: the
   * `workspace` state update and the request that depends on it happen in the
   * same tick, so an effect-driven value is one commit too late — which is
   * precisely how `/locations` came to answer for the previous workspace.
   */
  const activeWorkspaceIdRef = useRef<string | null>(null);

  /** Point every subsequent request at `workspaceId`, immediately. */
  const adoptWorkspaceId = useCallback((workspaceId: string | null) => {
    activeWorkspaceIdRef.current = workspaceId;
    setWorkspaceId(workspaceId);
  }, []);

  // Fetch user's accessible workspaces with roles
  const fetchAccessibleWorkspaces = useCallback(async () => {
    try {
      const response = await apiGet<UserWorkspaceAccess[]>('/workspaces/me/accessible');
      if (response.success && response.data) {
        setAccessibleWorkspaces(response.data);
        setError(null);
        return response.data;
      }
    } catch (err) {
      const message = getErrorMessage(err);
      console.error('Failed to fetch accessible workspaces:', err);
      setError(message);
    }
    return [];
  }, []);

  // Fetch workspace data from API
  const fetchWorkspace = useCallback(async () => {
    try {
      const response = await apiGet<{ workspace: Workspace; role?: UserRole }>('/workspaces/current');
      if (response.success && response.data?.workspace) {
        setWorkspace(response.data.workspace);
        if (response.data.role) {
          setCurrentRole(response.data.role);
        }
        setError(null);
        return response.data.workspace;
      }
    } catch (err) {
      const message = getErrorMessage(err);
      console.error('Failed to fetch workspace:', err);
      setError(message);
    }
    return null;
  }, []);

  /**
   * Fetch the current workspace's locations and auto-select one.
   *
   * `forWorkspaceId` is not optional decoration: `/locations` answers for
   * whichever workspace the request header names, and the header defaults to
   * a module-level value synced from an effect. A caller that has just
   * changed workspaces must say which one it means, or it gets the previous
   * workspace's locations and pins one of them as the current location — the
   * production defect. `withWorkspace()` puts it on the request itself and
   * the interceptor leaves it alone.
   */
  const fetchLocation = useCallback(async (forWorkspaceId?: string) => {
    const targetWorkspaceId = forWorkspaceId ?? activeWorkspaceIdRef.current;
    try {
      const response = await apiGet<{ locations: Location[] }>(
        '/locations',
        targetWorkspaceId ? withWorkspace(targetWorkspaceId) : undefined
      );
      // Naming the workspace fixes which locations come back; it does not fix
      // WHEN. A response for a workspace the user has since left must not
      // win by arriving last.
      if (targetWorkspaceId !== activeWorkspaceIdRef.current) {
        return null;
      }
      const locations = response.data?.locations;
      if (response.success && locations && locations.length > 0) {
        // Get the first active location as default
        const activeLocation = locations.find(loc => loc.isActive) || locations[0];
        setLocation(activeLocation);
        setError(null);
        return activeLocation;
      }
    } catch (err) {
      const message = getErrorMessage(err);
      console.error('Failed to fetch location:', err);
      setError(message);
    }
    return null;
  }, []);

  // Permission helper: check if user has at least the specified role
  const hasRole = useCallback((minimumRole: UserRole): boolean => {
    if (!currentRole) return false;
    const currentIndex = RoleHierarchy.indexOf(currentRole);
    const minimumIndex = RoleHierarchy.indexOf(minimumRole);
    return currentIndex >= minimumIndex;
  }, [currentRole]);

  // Permission helper: check if user can access a specific workspace
  const canAccessWorkspace = useCallback((workspaceId: string): boolean => {
    return accessibleWorkspaces.some(w => w.workspace.id === workspaceId);
  }, [accessibleWorkspaces]);

  // Permission helper: check if user can access a specific location.
  //
  // This used to answer `true` for workspace admins before looking at
  // the id at all, which made it a role test rather than a location test: it
  // approved a location of ANOTHER workspace. `/workspaces/me/accessible`
  // already gives those roles every location of the workspace, so membership
  // in the scope is the same answer for them and the correct one for a
  // foreign id.
  const canAccessLocation = useCallback((locationId: string): boolean => {
    return isLocationInScope(locationScope, locationId);
  }, [locationScope]);

  // Get user's role in a specific workspace
  const getWorkspaceRole = useCallback((workspaceId: string): UserRole | null => {
    const workspaceAccess = accessibleWorkspaces.find(w => w.workspace.id === workspaceId);
    return workspaceAccess?.role || null;
  }, [accessibleWorkspaces]);

  // Get user's access level for a specific location
  const getLocationAccessLevel = useCallback((locationId: string): LocationAccessLevel | null => {
    if (!workspace) return null;
    const workspaceAccess = accessibleWorkspaces.find(w => w.workspace.id === workspace.id);
    if (!workspaceAccess) return null;

    // A workspace admin has admin access to every location
    if (workspaceAccess.role === 'workspace-admin') {
      return 'admin';
    }

    const locationAccess = workspaceAccess.locations.find(l => l.id === locationId);
    return (locationAccess?.accessLevel as LocationAccessLevel) || null;
  }, [workspace, accessibleWorkspaces]);

  // Switch to a different workspace
  const switchWorkspace = useCallback(async (
    workspaceId: string,
    options?: { skipAccessCheck?: boolean }
  ): Promise<boolean> => {
    // Skip access check for newly created workspaces (API will validate)
    if (!options?.skipAccessCheck && !canAccessWorkspace(workspaceId)) {
      console.error('User does not have access to workspace:', workspaceId);
      return false;
    }

    try {
      const response = await apiPost<{ workspace: Workspace; role: UserRole }>(`/workspaces/${workspaceId}/switch`, {});
      if (response.success && response.data) {
        const nextWorkspace = response.data.workspace;
        setWorkspace(nextWorkspace);
        setCurrentRole(response.data.role);
        // Reset location when switching workspace
        setLocation(null);
        // Address the new workspace NOW. The effect below does the same thing
        // one commit later, which is after the request on the next line has
        // already gone out.
        adoptWorkspaceId(nextWorkspace.id);
        // Fetch locations for the new workspace, naming it explicitly
        await fetchLocation(nextWorkspace.id);
        return true;
      }
    } catch (error) {
      console.error('Failed to switch workspace:', error);
    }
    return false;
  }, [canAccessWorkspace, fetchLocation, adoptWorkspaceId]);

  // Switch to a different location (or null for "All Locations")
  const switchLocation = useCallback(async (locationId: string | null): Promise<boolean> => {
    if (locationId && !canAccessLocation(locationId)) {
      console.error('User does not have access to location:', locationId);
      return false;
    }

    if (!locationId) {
      setLocation(null);
      // Note: We don't persist "All Locations" selection since it's a UI convenience
      return true;
    }

    // Find location in accessible locations
    const foundLocation = accessibleLocations.find(l => l.id === locationId);
    if (foundLocation && workspace) {
      // Call API to persist the selection to database
      try {
        await apiPost(`/workspaces/${workspace.id}/locations/${locationId}/switch`, {});
        setLocation(foundLocation);
        return true;
      } catch (error) {
        console.error('Failed to switch location:', error);
        // Still set locally even if persist fails
        setLocation(foundLocation);
        return true;
      }
    }

    return false;
  }, [canAccessLocation, accessibleLocations, workspace]);

  // Refresh functions for external use
  const refreshWorkspace = useCallback(async () => {
    await fetchWorkspace();
  }, [fetchWorkspace]);

  const refreshLocation = useCallback(async () => {
    // Re-read the membership list first. It is what defines the switcher's
    // options and the scope the location invariant is judged against, and
    // nothing else re-reads it after Settings creates or deletes a location —
    // so auto-selecting from /locations alone would pick something the
    // switcher cannot offer, which is the very mismatch this file now
    // forbids.
    await fetchAccessibleWorkspaces();
    await fetchLocation();
  }, [fetchAccessibleWorkspaces, fetchLocation]);

  const refreshAccessibleWorkspaces = useCallback(async () => {
    await fetchAccessibleWorkspaces();
  }, [fetchAccessibleWorkspaces]);

  // Update current role when workspace changes
  useEffect(() => {
    if (workspace) {
      const role = getWorkspaceRole(workspace.id);
      if (role) {
        setCurrentRole(role);
      }
    }
  }, [workspace, getWorkspaceRole]);

  // Sync workspace ID to api-client for all API requests
  useEffect(() => {
    adoptWorkspaceId(workspace?.id ?? null);
  }, [workspace, adoptWorkspaceId]);

  /**
   * THE invariant: `location` is never a location outside the current
   * workspace's scope. Enforced here, once, rather than at each setter —
   * `setLocation` is exported on the context, so any consumer (and every
   * future fetch path) can otherwise reintroduce the defect. Dropping to
   * `null` is the "All Locations" state the switcher already renders, which
   * is the honest answer: the held location is not one of the options.
   */
  useEffect(() => {
    if (shouldClearLocation(locationScope, location?.id)) {
      setLocation(null);
    }
  }, [locationScope, location]);

  // Initial fetch - only when authenticated
  useEffect(() => {
    async function init() {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      // Only fetch if authenticated
      if (!isAuthenticated) {
        setWorkspace(null);
        setLocation(null);
        setCurrentRole(null);
        setAccessibleWorkspaces([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Step 1: First fetch accessible workspaces (doesn't require workspace ID header)
        const workspacesResult = await fetchAccessibleWorkspaces();

        if (!workspacesResult || workspacesResult.length === 0) {
          setIsLoading(false);
          return;
        }

        // Step 2: Try to restore user's saved workspace/location selection from database
        try {
          const savedResponse = await apiGet<{
            workspace: Workspace;
            location: Location | null;
            role: UserRole;
          } | null>('/workspaces/me/current-selection');

          if (savedResponse.success && savedResponse.data) {
            // User has a saved selection - use it
            const { workspace: savedWorkspace, location: savedLocation, role } = savedResponse.data;

            // Verify the saved workspace is still accessible
            const hasAccess = workspacesResult.some(w => w.workspace.id === savedWorkspace.id);
            if (hasAccess) {
              setWorkspace(savedWorkspace);
              setCurrentRole(role);
              adoptWorkspaceId(savedWorkspace.id);
              if (savedLocation) {
                setLocation(savedLocation);
              } else {
                await fetchLocation(savedWorkspace.id);
              }
              return;
            }
          }
        } catch (error) {
          console.warn('Could not fetch saved selection, using default:', error);
        }

        // Step 3: Fallback - use first workspace if no saved selection
        const firstWorkspace = workspacesResult[0];
        // The context projection, as /me/current-selection's above: with the
        // zone and the fiscal month (a switcher-only shape once left them out).
        setWorkspace(firstWorkspace.workspace);
        setCurrentRole(firstWorkspace.role);
        adoptWorkspaceId(firstWorkspace.workspace.id);
        await fetchLocation(firstWorkspace.workspace.id);
      } finally {
        setIsLoading(false);
      }
    }
    init();
  }, [fetchAccessibleWorkspaces, fetchLocation, isAuthenticated, authLoading, adoptWorkspaceId]);

  const value = useMemo(() => ({
    workspace,
    location,
    isLoading,
    currentRole,
    accessibleWorkspaces,
    accessibleLocations,
    hasRole,
    canAccessWorkspace,
    canAccessLocation,
    getWorkspaceRole,
    getLocationAccessLevel,
    setWorkspace,
    setLocation,
    switchWorkspace,
    switchLocation,
    refreshWorkspace,
    refreshLocation,
    refreshAccessibleWorkspaces,
    error,
    clearError,
  }), [
    workspace,
    location,
    isLoading,
    currentRole,
    accessibleWorkspaces,
    accessibleLocations,
    hasRole,
    canAccessWorkspace,
    canAccessLocation,
    getWorkspaceRole,
    getLocationAccessLevel,
    switchWorkspace,
    switchLocation,
    refreshWorkspace,
    refreshLocation,
    refreshAccessibleWorkspaces,
    error,
    clearError,
  ]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
